import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import { useTranslation } from '../i18n';
import './PreferencesPage.css';

export const PreferencesPage: React.FC = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [experience, setExperience] = useState('');
  const [goals, setGoals] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [duration, setDuration] = useState<number | undefined>();

  const experiences = [
    { id: 'beginner', label: t.onboarding.beginner, description: t.onboarding.beginnerDesc },
    { id: 'intermediate', label: t.onboarding.intermediate, description: t.onboarding.intermediateDesc },
    { id: 'advanced', label: t.onboarding.advanced, description: t.onboarding.advancedDesc },
  ];

  const goalOptions = [
    { id: 'reduce_stress', label: t.onboarding.reduceStress, icon: '🪷' },
    { id: 'improve_sleep', label: t.onboarding.improveSleep, icon: '🌙' },
    { id: 'increase_focus', label: t.onboarding.increaseFocus, icon: '🍃' },
    { id: 'manage_emotions', label: t.onboarding.manageEmotions, icon: '🕊️' },
    { id: 'find_peace', label: t.onboarding.findPeace, icon: '☮️' },
  ];

  const typeOptions = [
    { id: 'mindfulness', label: t.onboarding.mindfulness, icon: '🪷' },
    { id: 'breathing', label: t.onboarding.breathingPractices, icon: '🌬️' },
    { id: 'visualization', label: t.onboarding.visualization, icon: '🌅' },
    { id: 'mantras', label: t.onboarding.mantras, icon: '🕉️' },
    { id: 'body_scan', label: t.onboarding.bodyScan, icon: '✨' },
  ];

  const durationOptions = [
    { value: 5, label: `5 ${t.common.minutes}` },
    { value: 10, label: `10 ${t.common.minutes}` },
    { value: 15, label: `15 ${t.common.minutes}` },
    { value: 20, label: `20 ${t.common.minutes}` },
    { value: 30, label: `30+ ${t.common.minutes}` },
  ];

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      const prefs = await apiClient.getPreferences();
      setExperience(prefs.meditation_experience || '');
      setGoals(prefs.meditation_goals || []);
      setTypes(prefs.meditation_types || []);
      setDuration(prefs.meditation_duration_preference);
    } catch (error) {
      console.error('Failed to load preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoalToggle = (goalId: string) => {
    setGoals(prev => 
      prev.includes(goalId) 
        ? prev.filter(g => g !== goalId)
        : [...prev, goalId]
    );
  };

  const handleTypeToggle = (typeId: string) => {
    setTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(t => t !== typeId)
        : [...prev, typeId]
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await apiClient.updatePreferences({
        meditation_experience: experience,
        meditation_goals: goals,
        meditation_types: types,
        meditation_duration_preference: duration,
      });
      
      // Haptic feedback
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
      
      navigate('/profile');
    } catch (error) {
      console.error('Failed to update preferences:', error);
      
      // Haptic feedback for error
      if (window.Telegram?.WebApp?.HapticFeedback) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container preferences-page">
        <div className="preferences-loading">{t.common.loading}</div>
      </div>
    );
  }

  return (
    <div className="page-container preferences-page">
      <div className="preferences-header">
        <button className="back-button" onClick={() => navigate('/profile')}>
          <img src="/back.svg" alt={t.common.back} className="back-icon" />
        </button>
        <h1 className="page-title">{t.preferences.title}</h1>
      </div>

      <div className="preferences-content">
        {/* Experience Section */}
        <div className="preference-section">
          <h2 className="section-title">{t.preferences.meditationExperience}</h2>
          <p className="section-description">{t.preferences.experienceDescription}</p>
          <div className="experience-options">
            {experiences.map(exp => (
              <div
                key={exp.id}
                className={`experience-card ${experience === exp.id ? 'selected' : ''}`}
                onClick={() => setExperience(exp.id)}
              >
                <div className="experience-card-content">
                  <div className="experience-card-title">{exp.label}</div>
                  <div className="experience-card-description">{exp.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Goals Section */}
        <div className="preference-section">
          <h2 className="section-title">{t.preferences.meditationGoals}</h2>
          <p className="section-description">{t.preferences.goalsDescription}</p>
          <div className="preferences-options-grid">
            {goalOptions.map(goal => (
              <div
                key={goal.id}
                className={`preferences-option-card ${goals.includes(goal.id) ? 'selected' : ''}`}
                onClick={() => handleGoalToggle(goal.id)}
              >
                <div className="preferences-option-icon">{goal.icon}</div>
                <div className="preferences-option-label">{goal.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Types Section */}
        <div className="preference-section">
          <h2 className="section-title">{t.preferences.meditationTypes}</h2>
          <p className="section-description">{t.preferences.typesDescription}</p>
          <div className="preferences-options-grid">
            {typeOptions.map(type => (
              <div
                key={type.id}
                className={`preferences-option-card ${types.includes(type.id) ? 'selected' : ''}`}
                onClick={() => handleTypeToggle(type.id)}
              >
                <div className="preferences-option-icon">{type.icon}</div>
                <div className="preferences-option-label">{type.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Duration Section */}
        <div className="preference-section">
          <h2 className="section-title">{t.preferences.sessionDuration}</h2>
          <p className="section-description">{t.preferences.durationDescription}</p>
          <div className="duration-options">
            {durationOptions.map(dur => (
              <div
                key={dur.value}
                className={`duration-card ${duration === dur.value ? 'selected' : ''}`}
                onClick={() => setDuration(dur.value)}
              >
                {dur.label}
              </div>
            ))}
          </div>
        </div>

        <button 
          className="save-preferences-button" 
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? t.common.saving : t.preferences.saveChanges}
        </button>
      </div>
    </div>
  );
};
